import { useState, useEffect, useRef } from 'react';
import { Checkbox, Contact, DateInput, NumberInput, Text, Toggle } from './FieldComponents';
import { getBillingEntryFlagEditorValue } from './billingEntryMutation';

const EMPTY_LIST = [];
const EMPTY_ENTRY = {};
const NOOP = () => {};

export const CreateEntry = ({ users = EMPTY_LIST, entryData = EMPTY_ENTRY, setEntryData = NOOP, rateEntries = EMPTY_LIST, embedded = false }) => {
    const [selectedUser, setSelectedUser] = useState(null);
    const [rateManuallyEdited, setRateManuallyEdited] = useState(false);
    const autoFilledRateRef = useRef(null);

    const selectedUserId = selectedUser && typeof selectedUser === 'object'
        ? selectedUser.id
        : selectedUser;

    const safeUsers = Array.isArray(users) ? users : EMPTY_LIST;
    const safeRateEntries = Array.isArray(rateEntries) ? rateEntries : EMPTY_LIST;
    const selectedRateEntry = safeRateEntries.find(
        rateEntry => rateEntry && Number(rateEntry.user_id) === Number(selectedUserId)
    );
    const defaultBillingFlag = !entryData.item_type || entryData.item_type === 'time';
    const draftValue = getBillingEntryFlagEditorValue(entryData.draft, false);
    const billableValue = getBillingEntryFlagEditorValue(entryData.billable, defaultBillingFlag);
    const chargeableValue = getBillingEntryFlagEditorValue(entryData.chargeable, defaultBillingFlag);
    const nullableNumberValue = (value) => value === '' ? null : Number(value);

    useEffect(() => {
        if (entryData.id) return;

        const shouldResetManualEdit = !selectedUserId && (
            !rateManuallyEdited
            || (autoFilledRateRef.current !== null && Number(entryData.rate) === Number(autoFilledRateRef.current))
        );

        if (shouldResetManualEdit) {
            setRateManuallyEdited(false);
        }

        setEntryData(prev => {
            if (!selectedUserId) {
                const shouldClearRate = !rateManuallyEdited
                    || (autoFilledRateRef.current !== null && Number(prev.rate) === Number(autoFilledRateRef.current));

                if (shouldClearRate) {
                    autoFilledRateRef.current = null;
                }

                return {
                    ...prev,
                    user_id: null,
                    billing_rate_id: null,
                    rate: shouldClearRate ? null : prev.rate
                };
            }

            const shouldAutofillRate = !rateManuallyEdited && selectedRateEntry?.rate !== undefined;
            if (shouldAutofillRate) {
                autoFilledRateRef.current = selectedRateEntry.rate;
            }

            return {
                ...prev,
                user_id: selectedUserId,
                billing_rate_id: selectedRateEntry?.id ?? prev.billing_rate_id ?? null,
                rate: shouldAutofillRate ? selectedRateEntry.rate : prev.rate
            };
        });
    }, [selectedUserId, selectedRateEntry?.id, selectedRateEntry?.rate, entryData.id, entryData.rate, rateManuallyEdited, setEntryData]);

    // Set selectedUser when editing an existing entry
    useEffect(() => {
        const effectiveUserId = entryData.effective_user_id
            ?? entryData.user_id
            ?? entryData.billing_rate_user_id;
        if (entryData.id && effectiveUserId) {
            const user = safeUsers.find(u => Number(u?.id) === Number(effectiveUserId))
                || (entryData.user_name ? { id: effectiveUserId, name: entryData.user_name } : null);
            if (user) setSelectedUser(previous => user !== previous ? user : previous);
        }
    }, [entryData.billing_rate_user_id, entryData.effective_user_id, entryData.id, entryData.user_id, entryData.user_name, safeUsers]);

    const handleSelectedUserChange = (nextUser) => {
        setSelectedUser(nextUser);
        if (!entryData.id) return;

        const nextUserId = nextUser && typeof nextUser === 'object' ? nextUser.id : nextUser;
        const normalizedUserId = Number(nextUserId) > 0 ? Number(nextUserId) : null;
        const nextRateEntry = safeRateEntries.find(
            rateEntry => rateEntry && Number(rateEntry.user_id) === normalizedUserId
        );
        setEntryData(previous => ({
            ...previous,
            user_id: normalizedUserId,
            effective_user_id: normalizedUserId,
            billing_rate_user_id: nextRateEntry?.user_id ?? null,
            billing_rate_id: nextRateEntry?.id ?? null,
            rate: nextRateEntry?.rate ?? previous.rate,
        }));
    };

    return (
        <div className={embedded ? 'modal-content-section billing-entry-editor-content' : 'modal-content-wrapper'}>
            <div className='form-group mid team-member'>
                <label className='subtext'>Team Member*</label>
                <Contact
                    selectedContact={selectedUser}
                    setSelectedContact={handleSelectedUserChange}
                    users={safeUsers}
                />
            </div>
            <div className='entry-container'>
                <div className='form-group nm'>
                    <label className='subtext'>Date*</label>
                    <DateInput
                        value={entryData.date ?? ''}
                        onChange={(val) => setEntryData({ ...entryData, date: val })}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Rate*</label>
                    <NumberInput
                        type='number'
                        value={entryData.rate ?? selectedRateEntry?.rate ?? ''}
                        onChange={(val) => {
                            setRateManuallyEdited(true);
                            setEntryData({ ...entryData, rate: nullableNumberValue(val) });
                        }}
                    />
                </div>
                <div className='form-group nm'>
                    <label className='subtext'>Hours*</label>
                    <NumberInput
                        type='number'
                        value={entryData.hours ?? ''}
                        onChange={(val) => setEntryData({
                            ...entryData,
                            hours: nullableNumberValue(val),
                            raw_quantity: nullableNumberValue(val),
                            quantity: nullableNumberValue(val),
                        })}
                    />
                </div>
            </div>
            <div className='form-group mid'>
                <label className='subtext'>Description*</label>
                <Text
                    type='textarea'
                    value={entryData.description ?? ''}
                    onChange={(val) => setEntryData({ ...entryData, description: val })}
                    placeholder='Description...'
                />
            </div>
            <Checkbox
                value={draftValue}
                onChange={() => setEntryData(prev => ({ ...prev, draft: !draftValue }))}
                label='Save as Draft'
                hint='Enable to save this entry as a draft. It will not be included in the invoice.'
                space
            />
            <div className='entry-lower-container'>
                <Toggle
                    label='Billable'
                    value={billableValue}
                    onChange={() => setEntryData(prev => ({ ...prev, billable: !billableValue }))}
                />
                <Toggle
                    label='Chargeable'
                    value={chargeableValue}
                    onChange={() => setEntryData(prev => ({ ...prev, chargeable: !chargeableValue }))}
                />
            </div>
        </div>
    );
};
